// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

// eslint-disable-next-line spire/fenced-imports
import { hideAll } from "tippy.js";

import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { PageLinkModel } from "@insite/client-framework/Services/ContentService";
import { getIsPunchOutSession, getSession } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getCurrentBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import loadCurrentBillTo from "@insite/client-framework/Store/Data/BillTos/Handlers/LoadCurrentBillTo";
import loadCurrentShipTo from "@insite/client-framework/Store/Data/ShipTos/Handlers/LoadCurrentShipTo";
import { getCurrentShipToState } from "@insite/client-framework/Store/Data/ShipTos/ShipTosSelectors";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import AddressInfoDisplay from "@insite/content-library/Components/AddressInfoDisplay";
import Button from "@insite/mobius/Button";
import Clickable from "@insite/mobius/Clickable";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import LoadingSpinner from "@insite/mobius/LoadingSpinner";
import Typography from "@insite/mobius/Typography";
import { useHistory } from "@insite/mobius/utilities/HistoryContext";
import { miniMyAccountStyles, MiniMyAccountStylesInterface } from "@stanion/App/Components/MiniMyAccount/style";
import { mergeWithCss } from "@stanion/XDL/Utilities";
import React, { FC, useEffect } from "react";
import { useDispatch } from "react-redux";

interface MiniSignInProps {
    linkGroups?: {
        title: string;
        links: (
            | {
                title: string;
                url: string;
                type?: string | undefined;
                id: string;
                parentId?: string | undefined;
                excludeFromNavigation?: boolean | undefined;
                children?: PageLinkModel[] | undefined;
            }
            | undefined
        )[];
    }[];
    displayChangeCustomerLink?: boolean;
    onSignOutHandler?: () => void;
    extendedStyles?: MiniMyAccountStylesInterface;
}

type Props = MiniSignInProps;

const MiniMyAccount: FC<Props> = ({ linkGroups, onSignOutHandler, extendedStyles }) => {
    const [styles] = React.useState(() => mergeWithCss(miniMyAccountStyles, extendedStyles));

    const [isLoading, setIsLoading] = React.useState(false);

    const history = useHistory();

    const {
        currentBillTo,
        currentShipTo,
        billToId,
        isPunchOutSession,
        displayChangeCustomerLink,
        changeCustomerUrl,
        firstName,
        myAccountPageLink,
        accountManager,
        myBranch,
    } = useAppSelector(state => {
        const session = getSession(state);
        const billto = getCurrentBillToState(state);

        const accountManager = JSON.parse(session?.properties["accountManager"] || "{}");
        const myBranch = JSON.parse(session?.properties["myBranch"] || "{}");

        return {
            currentBillTo: billto.value,
            currentShipTo: getCurrentShipToState(state).value,
            billToId: billto.id,
            displayChangeCustomerLink: session.displayChangeCustomerLink,
            changeCustomerUrl: getPageLinkByPageType(state, "ChangeCustomerPage")?.url,
            isPunchOutSession: getIsPunchOutSession(state),
            firstName: session.firstName,
            myAccountPageLink: getPageLinkByPageType(state, "MyAccountPage"),
            accountManager,
            myBranch,
        };
    });

    const dispatch = useDispatch();

    useEffect(() => {
        if (billToId && !currentBillTo) {
            dispatch(loadCurrentBillTo());
            dispatch(loadCurrentShipTo());
        }
    }, []);

    const linksSection = linkGroups?.map((linkGroup, index) => {
        if (linkGroup.links.length > 0) {
            return (
                // eslint-disable-next-line react/no-array-index-key
                <StyledWrapper {...styles.linkItems?.wrapper} key={index}>
                    {linkGroup.title && <Typography {...styles.linkItems?.title}>{linkGroup.title}</Typography>}
                    {linkGroup.links.map(
                        link =>
                            link?.url && (
                                <StyledWrapper key={`${link?.title}_${link?.url}`} {...styles.linkItems?.linkWrapper}>
                                    <Link href={link?.url} {...styles.linkItems?.link}>
                                        {link?.title}
                                    </Link>
                                </StyledWrapper>
                            ),
                    )}
                </StyledWrapper>
            );
        }
        return null;
    });

    if (isLoading) {
        return (
            <StyledWrapper {...styles.spinnerWrapper}>
                <LoadingSpinner />
            </StyledWrapper>
        );
    }

    return (
        <StyledWrapper {...styles.wrapper}>
            <StyledWrapper {...styles.topSection}>
                <Clickable href={myAccountPageLink?.url || ""} onClick={() => hideAll()}>
                    <Typography {...styles.title}>
                        {translate("Hello")}, <Typography as="span">{firstName}!</Typography>
                    </Typography>
                </Clickable>

                <StyledWrapper {...styles.customerInformation}>
                    <StyledWrapper>
                        <Typography as="b">{translate("Customer #")}: </Typography> {currentBillTo?.customerNumber}
                    </StyledWrapper>

                    <StyledWrapper>
                        <Typography as="b">{translate("Default Shipping Address")}:</Typography>
                        <AddressInfoDisplay
                            address1={currentShipTo?.address1 || ""}
                            address2={currentShipTo?.address2}
                            city={currentShipTo?.city || ""}
                            state={currentShipTo?.state?.abbreviation}
                            country={currentShipTo?.country?.abbreviation}
                            postalCode={currentShipTo?.postalCode || ""}
                            extendedStyles={styles.addressStyles}
                        ></AddressInfoDisplay>
                    </StyledWrapper>

                    {displayChangeCustomerLink && (
                        <Button
                            {...styles.changeCustomerButton}
                            aria-label="changeCustomer"
                            onClick={() => {
                                changeCustomerUrl && history.push(changeCustomerUrl);
                                hideAll();
                            }}
                        >
                            {translate("Change Ship-To")}
                        </Button>
                    )}
                </StyledWrapper>
            </StyledWrapper>

            <StyledWrapper {...styles.contentWrapper}>
                {linksSection}

                <StyledWrapper>
                    <Typography {...styles.linkItems?.title}>{translate("Profile Information")}</Typography>
                    <StyledWrapper {...styles.profileInfo?.wrapper}>
                        {(accountManager.Name || accountManager.Phone1 || accountManager.Phone2) && (
                            <StyledWrapper {...styles.profileInfo?.section}>
                                <Typography as="h6" {...styles.profileInfo?.title}>
                                    {translate("Account Manager")}
                                </Typography>
                                {accountManager.Name && (
                                    <Clickable href={`mailto:${accountManager.Email}`} {...styles.profileInfo?.link}>
                                        <Icon src="Mail" {...styles.profileInfo?.icon} />
                                        {accountManager.Name}
                                    </Clickable>
                                )}
                                {accountManager.Phone1 && (
                                    <Clickable href={`tel:${accountManager.Phone1}`} {...styles.profileInfo?.link}>
                                        <Icon src="Phone" {...styles.profileInfo?.icon} />
                                        {accountManager.Phone1}
                                    </Clickable>
                                )}
                                {accountManager.Phone2 && (
                                    <Clickable href={`tel:${accountManager.Phone2}`} {...styles.profileInfo?.link}>
                                        <Icon src="Phone" {...styles.profileInfo?.icon} />
                                        {accountManager.Phone2}
                                    </Clickable>
                                )}
                            </StyledWrapper>
                        )}
                        {(myBranch.ContactName || myBranch.Phone) && (
                            <StyledWrapper {...styles.profileInfo?.section}>
                                <Typography as="h6" {...styles.profileInfo?.title}>
                                    {translate("Branch Manager")}
                                </Typography>
                                {myBranch.ContactName && (
                                    <Clickable href={`mailto:${myBranch.ContactName}`} {...styles.profileInfo?.link}>
                                        <Icon src="Mail" {...styles.profileInfo?.icon} />
                                        {myBranch.ContactName}
                                    </Clickable>
                                )}
                                {myBranch.Phone && (
                                    <Clickable href={`tel:${accountManager.Phone}`} {...styles.profileInfo?.link}>
                                        <Icon src="Phone" {...styles.profileInfo?.icon} />
                                        {myBranch.Phone}
                                    </Clickable>
                                )}
                            </StyledWrapper>
                        )}
                    </StyledWrapper>
                </StyledWrapper>

                {/* Xng-changes : Hide Change Ship-To on web-only */}
                <Button
                    {...styles.signOutButton}
                    onClick={() => {
                        onSignOutHandler?.();
                        setIsLoading(true);
                        setTimeout(() => {
                            hideAll();
                            setIsLoading(false);
                        }, 500);
                    }}
                    aria-label="signOut"
                    data-test-selector="headerSignOutLink"
                >
                    {isPunchOutSession ? translate("Cancel PunchOut") : translate("Sign Out")}
                </Button>
            </StyledWrapper>
        </StyledWrapper>
    );
};

export default MiniMyAccount;
